import { Button } from '@any-ui-react/core';
import type { ApolloError } from '@apollo/client';
import { vestResolver } from '@hookform/resolvers/vest';
import { addYears } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { QuarterSelect } from '@/common/components/shared/quarter-select/QuarterSelect';
import { toast } from '@/common/components/shared/toaster/toast';
import { YearPicker } from '@/common/components/shared/year-picker/YearPicker';
import { type PerformanceReviewPeriodInput, useCreatePerformanceReviewMutation } from '@/common/graphql';
import { ApiUtils, DateUtils, PerformanceReviewUtils } from '@/common/utils';
import { performanceReviewFormValidator } from './performance-review-form.validator';

interface Props {
  id: string;
  onClose: () => void;
}

export const PerformanceReviewForm = ({ id, onClose }: Props) => {
  const { handleSubmit, control } = useForm<PerformanceReviewPeriodInput>({
    defaultValues: PerformanceReviewUtils.getCurrentPerformanceReviewPeriod(),
    resolver: vestResolver(performanceReviewFormValidator),
  });

  const [createPerformanceReview, { loading: isSubmitting }] = useCreatePerformanceReviewMutation({
    onCompleted: data => {
      if (data.createPerformanceReview.alreadyExists) {
        toast.warning('Performance review for this period already exists.');
      } else {
        toast.success('Performance review successfully created.');
      }
    },
    refetchQueries: ['getEmployee', 'getDashboardEmployee'],
    onError: error => {
      ApiUtils.handleApiError(error);
    },
  });

  const onCreatePerformanceReview = async (data: PerformanceReviewPeriodInput) => {
    try {
      if (id) {
        await createPerformanceReview({
          variables: {
            input: {
              employeeId: id,
              period: data,
            },
          },
        });
        onClose();
      }
    } catch (e) {
      ApiUtils.handleApiError(e as ApolloError);
    }
  };

  return (
    <form onSubmit={handleSubmit(onCreatePerformanceReview)}>
      <div className="mb-8 flex space-x-4">
        <Controller
          control={control}
          name="year"
          render={({ field: { onChange, value } }) => (
            <YearPicker
              label="Year"
              maxDate={addYears(new Date(), 1)}
              minDate={DateUtils.datePerformanceReviewStarts}
              value={String(value)}
              onChange={value => onChange(Number(value))}
            />
          )}
        />

        <Controller
          control={control}
          name="quarter"
          render={({ field: { onChange, value } }) => (
            <QuarterSelect label="Quarter" value={value} onChange={onChange} />
          )}
        />
      </div>

      <ModalFooter spacerSize={-1}>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button className="bg-blue-6" loading={isSubmitting} type="submit">
          Create
        </Button>
      </ModalFooter>
    </form>
  );
};
